import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Calendar as CalendarIcon, Newspaper, Clock, ArrowLeft, ChevronRight, Star, Award } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Article {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  readTime: string;
  image: string;
  category: string;
}

interface SponsoredProvider {
  id: number;
  name: string;
  service: string;
  rating: number;
  image: string;
}

interface CityData {
  name: string;
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  events: Array<{
    date: string;
    title: string;
    description: string;
  }>;
  news: Array<{
    id: number;
    title: string;
    date: string;
    content: string;
    image: string;
    author: string;
    readTime:string;
  }>;
  articles: Article[];
  sponsoredProviders: SponsoredProvider[];
}

interface CityHallClientProps {
  cityData: CityData;
  navigate: (path: string) => void;
}

// Global variable to track script loading state
let isLoading = false;
let isLoaded = false;
declare const google: any;

export default function CityHallClient({ cityData, navigate }: CityHallClientProps) {
  const [date, setDate] = useState<Date>(new Date());
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const mapRef = useRef<any | null>(null);
  const mapElementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const loadGoogleMaps = () => {
      if (isLoaded) {
        initMap();
        return;
      }

      if (isLoading) return;

      isLoading = true;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        isLoaded = true;
        isLoading = false;
        initMap();
      };

      document.head.appendChild(script);
    };

    loadGoogleMaps();
  }, []);

  const initMap = () => {
    if (
      !mapElementRef.current || 
      !cityData || 
      mapRef.current || 
      typeof google === "undefined"
    ) return;

    mapRef.current = new google.maps.Map(mapElementRef.current, {
      center: cityData.coordinates,
      zoom: 14,
    });

    new google.maps.Marker({
      position: cityData.coordinates,
      map: mapRef.current,
      title: cityData.name,
    });
  };

  const highlightedDays = cityData.events.map(event => new Date(event.date));

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="flex items-center gap-2 hover:bg-gray-100"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para Dashboard
        </Button>

        {/* Header */}
        <Card className="p-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4">{cityData.name}</h1>
          <div className="flex items-start gap-2 text-gray-600">
            <MapPin className="h-5 w-5 mt-1 flex-shrink-0" />
            <p>{cityData.address}</p>
          </div>
        </Card>

        {/* Sponsored Service Providers */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Award className="h-5 w-5 text-blue-600" />
            <h2 className="text-xl font-semibold">Prestadores em Destaque</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cityData.sponsoredProviders.map((provider) => (
              <Card key={provider.id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-4">
                  <div 
                    className="h-12 w-12 rounded-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${provider.image})` }}
                  />
                  <div>
                    <h3 className="font-semibold">{provider.name}</h3>
                    <p className="text-sm text-gray-600">{provider.service}</p>
                    <div className="flex items-center mt-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < provider.rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Card>

        {/* Featured Articles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cityData.articles.map((article) => (
            <Card key={article.id} className="overflow-hidden group">
              <div 
                className="h-48 bg-cover bg-center"
                style={{ backgroundImage: `url(${article.image})` }}
              />
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {article.category}
                  </span>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Clock className="h-4 w-4 mr-1" />
                    {article.readTime}
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2 group-hover:text-blue-600 transition-colors">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    <p className="font-medium">{article.author}</p>
                    <p>{format(new Date(article.date), "dd 'de' MMMM", { locale: ptBR })}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    className="text-blue-600 hover:text-blue-700"
                    onClick={() => setSelectedArticle(article)}
                  >
                    Saiba mais
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Map and Calendar sections */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Map */}
          <Card className="lg:col-span-8 p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5" />
              <h2 className="text-xl font-semibold">Localização</h2>
            </div>
            <div ref={mapElementRef} className="w-full h-[400px] rounded-lg" />
          </Card>

          {/* Calendar */}
          <Card className="lg:col-span-4 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarIcon className="h-5 w-5" />
              <h2 className="text-xl font-semibold">Eventos</h2>
            </div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(day) => {
                if (day) {
                  setDate(day);
                }
              }}
              className="rounded-md border"
              modifiers={{
                highlighted: highlightedDays
              }}
              modifiersStyles={{
                highlighted: { backgroundColor: '#3b82f6', color: 'white' }
              }}
            />
            <div className="mt-4">
              {cityData.events
                .filter(event => format(new Date(event.date), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd'))
                .map((event, index) => (
                  <div key={index} className="p-2 bg-blue-50 rounded-md mt-2">
                    <p className="font-medium">{event.title}</p>
                    <p className="text-sm text-gray-600">
                      {format(new Date(event.date), "dd 'de' MMMM", { locale: ptBR })}
                    </p>
                    <p className="font-small">{event.description}</p>
                  </div>
                ))}
            </div>
          </Card>
        </div>

        {/* Latest News */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Newspaper className="h-5 w-5" />
            <h2 className="text-xl font-semibold">Últimas Publicações</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {cityData.news.map((news) => (
              <Card key={news.id} className="overflow-hidden group">
                <div 
                  className="h-40 bg-cover bg-center"
                  style={{ backgroundImage: `url(${news.image})` }}
                />
                <div className="p-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <span>{news.author}</span>
                    <span>•</span>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {news.readTime}
                    </div>
                  </div>
                  <h3 className="font-semibold mb-2 group-hover:text-blue-600 transition-colors">
                    {news.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{news.content}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {format(new Date(news.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                    </span>
                    <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                      Saiba mais
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Card>
        
        {/* Article Dialog */}
        <Dialog open={!!selectedArticle} onOpenChange={() => setSelectedArticle(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{selectedArticle?.title}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div 
                className="w-full h-64 bg-cover bg-center rounded-lg mb-6"
                style={{ backgroundImage: `url(${selectedArticle?.image})` }}
              />
              <div className="flex items-center gap-4 mb-4">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{selectedArticle?.author}</span>
                  <span className="mx-2">•</span>
                  <span>{selectedArticle?.readTime}</span>
                </div>
                <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  {selectedArticle?.category}
                </span>
              </div>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedArticle?.content}
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}